import React from "react";
import Brightline from "../../assets/Brightline.jpg";
import VIARail from "../../assets/VIArail.jpg";
import Amtrak from "../../assets/Amtrak.jpg";
import {
  Train,
  Map,
  Wifi,
  Coffee,
  ChevronRight,
  ShieldCheck,
  CheckCircle2,
  Award,
  Star,
} from "lucide-react";

// --- Data ---

const premierBrands = [
  {
    name: "Amtrak",
    region: "United States",
    description:
      "Connecting over 500 destinations across 46 states. Experience the high-speed Acela corridor or take a scenic cross-country journey.",
    image: Amtrak,
    theme: "from-blue-900 to-blue-950",
    features: [
      "Nationwide Coverage",
      "Acela High-Speed",
      "Private Roomettes",
      "Scenic Routes",
    ],
    rating: 4.8,
  },
  {
    name: "VIA Rail",
    region: "Canada",
    description:
      "Discover the breathtaking beauty of Canada from coast to coast. Renowned for its iconic 'Canadian' route and glass-domed observation cars.",
    image: VIARail,
    theme: "from-amber-600 to-amber-900",
    features: [
      "Transcontinental",
      "Dome Cars",
      "Sleeper Plus Class",
      "Sustainable",
    ],
    rating: 4.9,
  },
  {
    name: "Brightline",
    region: "Florida, USA",
    description:
      "Redefining train travel with a hospitality-first approach. High-speed, eco-friendly connections between Central and South Florida.",
    // Direct Wikimedia Commons link (highly reliable for hotlinking)
    image: Brightline,
    theme: "from-yellow-400 to-yellow-600",
    features: [
      "Premium Lounges",
      "Gourmet Bites",
      "Ultra-Fast WiFi",
      "Modern Fleet",
    ],
    rating: 4.9,
  },
];

const globalPartners = [
  { name: "Deutsche Bahn", country: "Germany", code: "DB" },
  { name: "Renfe", country: "Spain", code: "RENFE" },
  { name: "SNCF", country: "France", code: "SNCF" },
  { name: "Italo", country: "Italy", code: "NTV" },
  { name: "Eurostar", country: "Europe", code: "EST" },
  { name: "Trenitalia", country: "Italy", code: "TI" },
];

// --- Components ---

export default function PopularBrandsPage() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-indigo-100 selection:text-indigo-900">
      {/* Page Header */}
      <section className="bg-slate-900 px-4 py-16 sm:py-24 lg:px-8 text-center">
        <div className="mx-auto max-w-3xl">
          <h1 className="text-3xl font-bold tracking-tight text-white sm:text-5xl">
            World-Class Carriers, One Unified Platform.
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-300 sm:text-lg">
            We partner with the industry's most prestigious rail networks to
            bring you seamless booking, exclusive fares, and guaranteed premium
            seating.
          </p>
        </div>
      </section>

      {/* Premier Brands Section */}
      <section className="mx-auto -mt-10 max-w-7xl px-4 sm:px-6 lg:px-8 relative z-20">
        <div className="mb-10 text-center">
          <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Our Premier Partners
          </h2>
          <p className="mt-3 text-sm font-medium text-slate-500 sm:text-base">
            Experience the gold standard of North American rail travel.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {premierBrands.map((brand, idx) => (
            <div
              key={idx}
              className="group relative flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-200/50 transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-slate-300/60"
            >
              {/* Image Hero */}
              <div className="relative h-64 w-full overflow-hidden sm:h-72">
                <img
                  src={brand.image}
                  alt={`${brand.name} Train`}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div
                  className={`absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent opacity-90`}
                />

                {/* Badges */}
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-3 py-1 text-xs font-bold text-white backdrop-blur-md">
                    <Map className="h-3 w-3" />
                    {brand.region}
                  </span>
                </div>

                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-3xl font-extrabold text-white sm:text-4xl drop-shadow-md">
                    {brand.name}
                  </h3>
                  <div className="mt-2 flex items-center gap-1 text-amber-400">
                    <Star className="h-4 w-4 fill-current" />
                    <span className="text-sm font-bold text-white ml-1">
                      {brand.rating} / 5 Rating
                    </span>
                  </div>
                </div>
              </div>

              {/* Content Card */}
              <div className="flex flex-1 flex-col p-6 sm:p-8">
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base mb-6 flex-1">
                  {brand.description}
                </p>

                {/* Features Grid */}
                <div className="mb-8 grid grid-cols-2 gap-3">
                  {brand.features.map((feature, fIdx) => (
                    <div
                      key={fIdx}
                      className="flex items-center gap-2 text-sm font-medium text-slate-700"
                    >
                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                        <CheckCircle2 className="h-3 w-3" />
                      </div>
                      {feature}
                    </div>
                  ))}
                </div>

                {/* Action Button */}
                <button className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-50 py-3.5 text-sm font-bold text-indigo-600 transition-colors group-hover:bg-indigo-600 group-hover:text-white border border-slate-200 group-hover:border-indigo-600">
                  View {brand.name} Routes
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Global Partners Section */}
      {/* <section className="mx-auto mt-24 max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="mb-10 flex flex-col items-center justify-between gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-end">
          <div>
            <span className="mb-2 block text-xs font-bold uppercase tracking-widest text-indigo-600">
              Global Reach
            </span>
            <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
              International Networks
            </h2>
          </div>
          <p className="text-sm font-medium text-slate-500 sm:text-right max-w-sm">
            Seamlessly book tickets across Europe's most extensive rail
            operators directly through our platform.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {globalPartners.map((partner, idx) => (
            <div
              key={idx}
              className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-slate-100 bg-white p-6 text-center shadow-sm transition-all hover:-translate-y-1 hover:border-indigo-100 hover:shadow-md cursor-pointer"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-50 text-slate-400 group-hover:bg-indigo-50 group-hover:text-indigo-600">
                <Train className="h-6 w-6" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900">
                  {partner.name}
                </h4>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-1">
                  {partner.country}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section> */}

      {/* CTA Section */}
      {/* <section className="bg-indigo-600 px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <Award className="mx-auto mb-4 h-12 w-12 text-indigo-200" />
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            Ready to experience the difference?
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-indigo-100">
            Compare schedules and book your premium seats on Amtrak, VIA Rail,
            Brightline, and more in seconds.
          </p>
          <div className="mt-8">
            <button className="rounded-full bg-white px-8 py-4 text-base font-bold text-indigo-600 shadow-lg transition-transform hover:scale-105 hover:bg-slate-50">
              Start Your Search Now
            </button>
          </div>
        </div>
      </section> */}
    </div>
  );
}
